import React, { useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Badge } from "antd";
import "./DashboardSidebar.scss";
import { useSelector } from "react-redux";
import dashboardRoutes from "../../nav";

/**
 * Dashboard sidebar for desktop shell.
 * Static menu grouped by dashboard intent — does NOT depend on General.Menu backend data.
 * All paths reference existing routes from nav.js.
 */

const DashboardSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { General } = useSelector((state) => state);
  const { Menu, ShowUC } = General || {};

  const menuGroups = useMemo(() => {
    if (!Menu || !Array.isArray(Menu)) return [];

    const groupsMap = Menu.reduce((acc, item) => {
      const routeConfig = dashboardRoutes.find((r) => r.path === item.URL);
      if (routeConfig && routeConfig.isUpdating && !ShowUC) {
        return acc;
      }

      // Ẩn menu nếu UC_DEV là true nhưng ShowUC là false
      if (item.UC_DEV && !ShowUC) {
        return acc;
      }

      const gName = item.group?.name || "Khác";
      const gSTT = item.group?.STT || 999;

      if (!acc[gName]) {
        acc[gName] = {
          label: gName,
          STT: gSTT,
          items: [],
        };
      }

      acc[gName].items.push({
        label: item.Name,
        path: item.URL,
        STT: parseInt(item.STT) || 0,
        icon: item.icon || "fa-circle-dot",
        isChild: !!item.Parent_Id?._id?.$oid || !!item.Parent_Id?._id,
      });

      return acc;
    }, {});

    const groups = Object.values(groupsMap)
      .sort((a, b) => a.STT - b.STT)
      .map((group) => ({
        ...group,
        items: group.items.sort((a, b) => a.STT - b.STT),
      }));

    return groups;
  }, [Menu, ShowUC]);

  const allPaths = useMemo(() => {
    if (!Menu || !Array.isArray(Menu)) return [];
    return Menu.map((item) => item.URL);
  }, [Menu]);

  const isActive = (path) => {
    if (location.pathname === path) return true;
    if (location.pathname.startsWith(path + "/")) {
      const hasSpecificMatch = allPaths.some(
        (p) => p !== path && location.pathname.startsWith(p),
      );
      return !hasSpecificMatch;
    }
    return false;
  };

  return (
    <aside className="ds-sidebar" data-testid="dashboard-sidebar">
      <nav className="ds-sidebar__nav">
        {menuGroups.map((group, gi) => (
          <div className="ds-sidebar__group" key={gi}>
            <span className="ds-sidebar__group-label">{group.label}</span>
            <ul className="ds-sidebar__list">
              {group.items.map((item, ii) => (
                <li key={ii}>
                  <button
                    type="button"
                    className={
                      "ds-sidebar__item" +
                      (isActive(item.path) ? " ds-sidebar__item--active" : "") +
                      (item.isChild ? " ds-sidebar__item--child" : "")
                    }
                    onClick={() => navigate(item.path)}
                    data-testid={`sidebar-nav-${item.path.replace(/\//g, "-").slice(1)}`}
                  >
                    <i className={"fa " + item.icon + " ds-sidebar__icon"} />
                    <span className="ds-sidebar__label">{item.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
};

export default DashboardSidebar;
